/**
 * Bounty Extension Dialog
 * Allows bounty owners to extend expiration time with wallet payment validation
 */

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Clock, AlertCircle, CheckCircle2, Wallet, Loader2, Info } from "lucide-react";
import { bountyApi, walletApi } from "@/services/api";
import { useLocalization } from "@/hooks/useLocalization";
import { toast } from "sonner";

interface BountyExtensionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bountyId: string;
  userId: string;
  currentExpiresAt: string;
  onSuccess: () => void;
}

export const BountyExtensionDialog = ({
  isOpen,
  onClose,
  bountyId,
  userId,
  currentExpiresAt,
  onSuccess
}: BountyExtensionDialogProps) => {
  const [selectedHours, setSelectedHours] = useState<string>("12");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [extensionInfo, setExtensionInfo] = useState<any>(null);
  const [costInfo, setCostInfo] = useState<any>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [walletLoading, setWalletLoading] = useState(true);
  const { formatCurrency } = useLocalization();

  // Fetch wallet balance and extension info on mount
  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !userId) return;

      setChecking(true);
      setWalletLoading(true);

      try {
        // Load wallet balance
        const { data: balanceData, error: balanceError } = await walletApi.getBalance(userId);
        if (balanceData && !balanceError) {
          // get_wallet_balance returns an array (table), get the first item
          const balance = Array.isArray(balanceData) ? balanceData[0] : balanceData;
          setWalletBalance(balance?.balance_cents || 0);
        }

        // Check if bounty can be extended
        const { data: canExtendData, error: extendError } = await bountyApi.canExtendBounty(bountyId, userId);
        console.log('🔍 Extension check:', { canExtendData, extendError });
        console.log('🔍 Current expires_at:', currentExpiresAt);
        console.log('🔍 Current time:', new Date().toISOString());
        if (canExtendData && !extendError) {
          setExtensionInfo(canExtendData);
        }

        // Calculate cost for default hours
        const hours = parseInt(selectedHours);
        const { data: costData } = await bountyApi.calculateExtensionCost(userId, hours);
        if (costData) {
          setCostInfo(costData);
        }
      } catch (error) {
        console.error('Failed to load extension data:', error);
      } finally {
        setChecking(false);
        setWalletLoading(false);
      }
    };

    loadData();
  }, [isOpen, bountyId, userId, selectedHours]);

  // Update cost when hours change
  useEffect(() => {
    const updateCost = async () => {
      if (!userId || !extensionInfo?.can_extend) return;

      const hours = parseInt(selectedHours);
      const { data } = await bountyApi.calculateExtensionCost(userId, hours);
      if (data) {
        setCostInfo(data);
      }
    };

    updateCost();
  }, [selectedHours, userId, extensionInfo]);

  const handleExtend = async () => {
    if (!costInfo || !extensionInfo) return;

    setLoading(true);
    try {
      const hours = parseInt(selectedHours);
      const { data, error } = await bountyApi.extendBounty(bountyId, userId, hours);

      if (error) throw error;

      if (data?.success) {
        toast.success("Bounty extended successfully!", {
          description: `Added ${hours} hours. New deadline: ${new Date(data.new_expires_at).toLocaleString()}`
        });
        onSuccess();
        onClose();
      } else {
        throw new Error(data?.error || "Extension failed");
      }
    } catch (error: any) {
      toast.error("Extension failed", {
        description: error.message || "Please try again"
      });
    } finally {
      setLoading(false);
    }
  };

  const hasEnoughBalance = walletBalance >= (costInfo?.cost_cents || 0);
  const canExtend = extensionInfo?.can_extend && hasEnoughBalance;

  const newExpiresAt = new Date(currentExpiresAt);
  const hours = parseInt(selectedHours);
  newExpiresAt.setHours(newExpiresAt.getHours() + hours);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-3 pb-4 border-b border-border/50">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
              <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg shadow-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <span>Extend Bounty</span>
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            Give hunters more time to complete this bounty
          </DialogDescription>
        </DialogHeader>

        {checking ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Checking extension eligibility...</span>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Current Deadline */}
            <div className="p-4 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900 dark:to-gray-800 rounded-xl border border-border/50">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Current Deadline</div>
                  <div className="font-semibold text-base">
                    {new Date(currentExpiresAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                <Clock className="h-5 w-5 text-muted-foreground/50" />
              </div>
            </div>

            {/* Extension Info */}
            {!extensionInfo?.can_extend ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {extensionInfo?.reason || "This bounty cannot be extended"}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Extension Limits */}
                <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                      <Info className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 text-xs text-blue-700 dark:text-blue-300 space-y-0.5">
                      <div className="font-semibold text-sm text-blue-900 dark:text-blue-100">Extension Limits</div>
                      <div className="flex justify-between">
                        <span>Extensions used:</span>
                        <span className="font-semibold">{extensionInfo.extensions_used} / {extensionInfo.max_extensions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Remaining:</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">{extensionInfo.extensions_remaining}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hours Selection */}
                <div className="space-y-2">
                  <Label htmlFor="hours" className="text-sm font-medium">Extend by (hours)</Label>
                  <Select value={selectedHours} onValueChange={setSelectedHours}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 hours</SelectItem>
                      <SelectItem value="12">12 hours</SelectItem>
                      <SelectItem value="24">24 hours</SelectItem>
                      <SelectItem value="36">36 hours</SelectItem>
                      <SelectItem value="48">48 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* New Deadline Preview */}
                <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-2 border-amber-300 dark:border-amber-700 rounded-xl shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">New Deadline</div>
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0 text-xs">+{hours}h</Badge>
                  </div>
                  <div className="font-bold text-lg text-amber-900 dark:text-amber-100">
                    {newExpiresAt.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>

                {/* Cost Breakdown */}
                {costInfo && (
                  <div className="p-4 bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50 rounded-xl border border-border/50">
                    <div className="space-y-2.5">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Extension Cost</span>
                        <span className="text-xl font-bold text-primary">
                          {formatCurrency(costInfo.cost_cents)}
                        </span>
                      </div>
                      <div className="h-px bg-border/50" />
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Rate</span>
                        <span className="font-medium">{formatCurrency(costInfo.hourly_rate_cents)}/hour</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Your Plan</span>
                        <Badge variant={costInfo.is_pro ? "default" : "secondary"} className="font-medium">
                          {costInfo.is_pro ? "✨ Pro" : "Free"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}

                {/* Wallet Balance */}
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-2 border-emerald-300 dark:border-emerald-700 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg">
                        <Wallet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Wallet Balance</span>
                    </div>
                    {walletLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <span className={`text-lg font-bold ${hasEnoughBalance ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(walletBalance)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Insufficient Balance Warning */}
                {!hasEnoughBalance && (
                  <Alert variant="destructive" className="border-red-300 dark:border-red-700 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-semibold mb-1">Insufficient Balance</div>
                      <div className="text-xs text-red-700 dark:text-red-300">
                        You need {formatCurrency(costInfo?.cost_cents || 0)} but only have {formatCurrency(walletBalance)}.
                        <br />
                        Shortfall: {formatCurrency((costInfo?.cost_cents || 0) - walletBalance)}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Extension Button */}
                <Button
                  className="w-full h-12 text-base font-semibold bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-lg hover:shadow-xl transition-all"
                  size="lg"
                  onClick={handleExtend}
                  disabled={loading || !canExtend}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : !hasEnoughBalance ? (
                    <>
                      <AlertCircle className="h-5 w-5 mr-2" />
                      Insufficient Balance
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Extend Bounty - {formatCurrency(costInfo?.cost_cents || 0)}
                    </>
                  )}
                </Button>

                {!hasEnoughBalance && (
                  <Button
                    variant="outline"
                    className="w-full h-11 border-2 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 hover:border-emerald-300 dark:hover:border-emerald-700 transition-all"
                    onClick={() => window.location.href = '/deposit'}
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Add Funds to Wallet
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
